import { useMemo } from "react";
import {
  ComposedChart, Line, Bar,
  Tooltip, Legend, XAxis, YAxis,
  CartesianGrid, ResponsiveContainer, ReferenceLine
} from "recharts";
import { Link } from "react-router-dom";
import { BoltIcon } from "@heroicons/react/24/outline";

const parseDate = (dateStr) => {
  if (!dateStr || dateStr === "TOTAL") return new Date(0);
  const parts = dateStr.split("-");
  if (parts.length !== 3) return new Date(0);
  const [d, m, y] = parts;
  return new Date(`${y}-${m}-${d}`);
};

export default function AnalysisPage({ dsmData }) {
  const dailySummary = useMemo(() => {
    if (!dsmData || !Array.isArray(dsmData) || dsmData.length === 0) return [];

    const grouped = {};

    dsmData.forEach((d) => {
      // Skip the summary/total row if it exists in data
      if (!d.date || d.date === "TOTAL") return;

      const dateKey = d.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          payable: 0,
          receivable: 0,
          totalMwh: 0,
          unitRateSum: 0,
          count: 0,
          parsedDate: parseDate(dateKey)
        };
      }

      const val = parseFloat(d.total) || 0;
      if (val < 0) {
        grouped[dateKey].payable += Math.abs(val);
      } else {
        grouped[dateKey].receivable += val;
      }

      grouped[dateKey].totalMwh += Math.abs(parseFloat(d.devMwh) || 0);
      grouped[dateKey].unitRateSum += parseFloat(d.unitRate) || 0;
      grouped[dateKey].count += 1;
    });

    return Object.values(grouped)
      .sort((a, b) => a.parsedDate - b.parsedDate)
      .map((g) => ({
        ...g,
        avgMW: parseFloat((g.totalMwh / 24).toFixed(2)),
        avgRate: parseFloat((g.unitRateSum / g.count).toFixed(2)),
        payable: -parseFloat(g.payable.toFixed(2)), // Keep payable negative for chart
        receivable: parseFloat(g.receivable.toFixed(2)),
      }));
  }, [dsmData]);

  // Error State
  if (!dsmData || dsmData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <BoltIcon className="h-16 w-16 text-gray-300 mb-4" />
        <p className="text-gray-500 font-medium">No DSM data loaded from App</p>
        <Link to="/" className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 transition">
          Go to Upload
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <nav className="bg-white border-b px-6 py-4 flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center gap-8">
          <div className="font-black text-xl text-indigo-600">DSM_ANALYTICS</div>
          <Link to="/" className="text-slate-600 hover:text-indigo-600">Upload</Link>
          <Link to="/analysis" className="text-indigo-600 font-bold border-b-2 border-indigo-600">Analysis</Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 mt-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-8">DSM Performance Insights</h1>

        {/* METRIC CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <p className="text-sm text-slate-500 uppercase tracking-wider font-semibold">Total Payable</p>
            <p className="text-2xl font-bold text-red-600 mt-1">
              ₹{Math.abs(dailySummary.reduce((a, b) => a + b.payable, 0)).toLocaleString()}
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <p className="text-sm text-slate-500 uppercase tracking-wider font-semibold">Total Receivable</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              ₹{dailySummary.reduce((a, b) => a + b.receivable, 0).toLocaleString()}
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <p className="text-sm text-slate-500 uppercase tracking-wider font-semibold">Avg Unit Rate</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">
              ₹{(dailySummary.reduce((a, b) => a + b.avgRate, 0) / dailySummary.length || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* CHART SECTION */}
        <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 mb-8">
          <h2 className="text-lg font-bold text-slate-800 mb-6">Deviation & Rate Trend</h2>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dailySummary}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="date" fontSize={12} tickMargin={10} />
                <YAxis yAxisId="left" fontSize={12} orientation="left" stroke="#6366f1" />
                <YAxis yAxisId="right" fontSize={12} orientation="right" stroke="#f59e0b" />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
                <Legend verticalAlign="top" align="right" height={36}/>
                
                <Bar yAxisId="left" name="Receivable (₹)" dataKey="receivable" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" name="Payable (₹)" dataKey="payable" fill="#ef4444" radius={[0, 0, 4, 4]} />
                
                <Line yAxisId="right" name="Avg MW" type="monotone" dataKey="avgMW" stroke="#6366f1" strokeWidth={3} dot={{ r: 4 }} />
                <Line yAxisId="right" name="Rate" type="monotone" dataKey="avgRate" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4 }} />
                
                <ReferenceLine y={0} stroke="#000" strokeWidth={1} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* RULES TABLE */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-5 bg-slate-50 border-b font-bold text-slate-700">Reference DSM Rules</div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-6 py-4">Section</th>
                  <th className="px-6 py-4">Band (MW)</th>
                  <th className="px-6 py-4">Condition</th>
                  <th className="px-6 py-4">Rate Multiplier</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                <tr>
                  <td className="px-6 py-4 font-medium">Under Drawal</td>
                  <td className="px-6 py-4">≤10</td>
                  <td className="px-6 py-4">50.05 – 50.00 Hz</td>
                  <td className="px-6 py-4">90% – 50% of ACP</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Over Drawal</td>
                  <td className="px-6 py-4">&gt;10</td>
                  <td className="px-6 py-4">&lt;50 Hz</td>
                  <td className="px-6 py-4">150% – 200% of ACP</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
